/* eslint-disable no-nested-ternary */
import { faPlus } from "@fortawesome/free-solid-svg-icons/faPlus";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { AtiraEmpty } from "../../components/AtiraEmpty";
import { Flex } from "../../components/Flex";
import { SubHeader } from "../../components/SubHeader";
import { IntegrationKind } from "../../model/integration/types/IntegrationKind";
import { integrationSliceSelectors } from "../../redux/integration/integration.selector";
import { integrationActions } from "../../redux/integration/integration.slice";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { userSliceSelectors } from "../../redux/user/user.selector";
import { IntegrationAddModal } from "./components/IntegrationAddModal";
import { IntegrationAddModalFacebookMeta } from "./components/IntegrationAddModalFacebookMeta";
import { IntegrationAddModalGoogleDrive } from "./components/IntegrationAddModalGoogleDrive";
import { IntegrationAddModalWhatsapp } from "./components/IntegrationAddModalWhatsapp";
import { IntegrationFacebookStepper } from "./components/IntegrationFacebookStepper";
import { IntegrationFBSection } from "./components/sections/IntegrationFBSection";
import { IntegrationFacebookMetaSection } from "./components/sections/IntegrationFacebookMetaSection";
// import { IntegrationGoogleDriveSection } from "./components/sections/IntegrationGoogleDriveSection"; // paused - see section render below
import { IntegrationWhatsAppSection } from "./components/sections/IntegrationWhatsAppSection";
import { useIntegrationContext } from "./context/integration-context";
import { PageIcons } from "../../utils/GlobalIcons";
import { AtiraDropdown } from "../../components/AtiraDropdown";
import { Button } from "../../components/Button";
import { faBars } from "@fortawesome/free-solid-svg-icons/faBars";
import { MenuProps } from "../../utils/local-antd";
import { AtiraIcon } from "../../components/AtiraIcon";
import { Text } from "../../components/Text";
import { IntegrationWhatsAppDirectSection } from "./components/sections/IntegrationWhatsAppDirectSectionD";
import { IntegrationWhatsAppMetaSection } from "./components/sections/IntegrationWhatsAppMetaSection";
import { IntegrationAddModalWhatsappMeta } from "./components/IntegrationAddModalWhatsappMeta";
import { WACoexistenceSyncStatus } from "./components/whatsapp-chat/WACoexistenceSyncStatus";
import { WAReconnectAlert } from "./components/whatsapp-chat/WAReconnectAlert";
import { WHATSAPP_META_RECONNECT_PARAM } from "./utils/integration-utils";

const IntegrationRoute: React.FC = () => {
  const { t } = useTranslation(undefined);
  const dispatch = useAppDispatch();
  const {
    addModalVisible,
    setAddModalVisible,
    setAddModalWhatsAppVisible,
    addModalWhatsAppVisible,
    setAddModalWhatsAppMetaVisible,
    addModalWhatsAppMetaVisible,
    setAddModalFacebookVisible,
    addModalFacebookVisible,
    setAddModalGoogleDriveVisible,
    addModalGoogleDriveVisible,
    addModalFacebookMetaVisible,
    setAddModalFacebookMetaVisible,
    facebookMetaEditFormId,
  } = useIntegrationContext();

  const userIntegrations = useAppSelector(
    integrationSliceSelectors.selectUserIntegrations,
  );
  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const isOrgMember = useAppSelector(userSliceSelectors.selectIsUserOrgMember);

  const [searchParams, setSearchParams] = useSearchParams();

  const subHeaderActions: MenuProps["items"] = [
    {
      key: "integration.add",
      label: (
        <Flex alignItems="center" gap="s">
          <AtiraIcon icon={faPlus} color="main" />
          <Text color="main">{t("common.add")}</Text>
        </Flex>
      ),
      onClick: () => setAddModalVisible(true),
      hide: isOrgMember,
    },
  ].filter((action) => !action.hide);

  useEffect(() => {
    dispatch(integrationActions.getUserIntegrations({ userId }));
  }, [dispatch, userId]);

  useEffect(() => {
    if (!searchParams.has(WHATSAPP_META_RECONNECT_PARAM)) {
      return;
    }

    // Only the owner can reconnect; a member who follows the link just lands here
    if (!isOrgMember) {
      setAddModalWhatsAppMetaVisible(true);
    }

    setSearchParams(
      (params) => {
        params.delete(WHATSAPP_META_RECONNECT_PARAM);
        return params;
      },
      { replace: true },
    );
  }, [
    searchParams,
    setSearchParams,
    setAddModalWhatsAppMetaVisible,
    isOrgMember,
  ]);

  return (
    <Flex flexDirection="column">
      <SubHeader
        title={t("integration.title")}
        icon={PageIcons.Integration}
        extra={
          subHeaderActions.length ? (
            <AtiraDropdown
              items={subHeaderActions}
              placement="bottomRight"
              trigger={["click"]}
              destroyOnHidden
            >
              <Button
                icon={faBars}
                color="black"
                padding="s"
                backgroundColor="white"
                hoverBackgroundColor="lightMain"
                hover
              >
                {t("common.options")}
              </Button>
            </AtiraDropdown>
          ) : null
        }
      />

      {!userIntegrations.length ? (
        <AtiraEmpty description={t("integration.empty")} />
      ) : null}

      <Flex flexDirection="column" gap="m" padding="m">
        {userIntegrations.map((integration) => (
          <Flex key={integration._id} width={"100%"}>
            {integration.kind === IntegrationKind.FACEBOOK ? (
              <IntegrationFBSection />
            ) : integration.kind === IntegrationKind.FACEBOOK_META ? (
              <IntegrationFacebookMetaSection />
            ) : integration.kind === IntegrationKind.WHATSAPP ? (
              <IntegrationWhatsAppSection />
            ) : integration.kind === IntegrationKind.WHATSAPP_META ? (
              <Flex flexDirection="column" gap="s" width="100%">
                <WAReconnectAlert
                  onReconnect={() => setAddModalWhatsAppMetaVisible(true)}
                />
                <WACoexistenceSyncStatus />
                <IntegrationWhatsAppMetaSection />
              </Flex>
            ) : integration.kind === IntegrationKind.WHATSAPP_DIRECT ? (
              <IntegrationWhatsAppDirectSection />
            ) : // Paused: the Drive endpoints are disabled (needs Google's paid
            // CASA assessment), so this would be a dead-end card for any
            // already-connected integration. Restore once that's resolved.
            // integration.kind === IntegrationKind.GOOGLE_DRIVE ? (
            //   <IntegrationGoogleDriveSection />
            // )
            null}
          </Flex>
        ))}
      </Flex>

      <IntegrationAddModal
        open={addModalVisible}
        onClose={() => setAddModalVisible(false)}
      />

      <IntegrationAddModalWhatsapp
        open={addModalWhatsAppVisible}
        onClose={() => setAddModalWhatsAppVisible(false)}
      />

      <IntegrationAddModalWhatsappMeta
        open={addModalWhatsAppMetaVisible}
        onClose={() => setAddModalWhatsAppMetaVisible(false)}
      />

      <IntegrationFacebookStepper
        open={addModalFacebookVisible}
        onClose={() => setAddModalFacebookVisible(false)}
      />

      <IntegrationAddModalGoogleDrive
        open={addModalGoogleDriveVisible}
        onClose={() => setAddModalGoogleDriveVisible(false)}
      />

      <IntegrationAddModalFacebookMeta
        open={addModalFacebookMetaVisible}
        onClose={() => setAddModalFacebookMetaVisible(false)}
        editFormId={facebookMetaEditFormId}
      />
    </Flex>
  );
};

export default IntegrationRoute;
